app.component("transcriptionRecorder", {
    templateUrl: "app/src/js/components/transcription/transcription-recorder.html",
    controller: "TranscriptionRecorderController",
    bindings: {
        practiceId: "<",
        patientId: "<"
    }
});

app.controller("TranscriptionRecorderController", [
    "$scope", "$interval", "$timeout", "$mdDialog", "$q", "CurrentPatient", "Session", "ChunkedRecordingService", "TranscriptionAPI", "TranscriptionSeatsAPI", "$modal", "PaymentTransactionsAPI", "AppointmentAPI", "TranscriptionLiveUpdateService", "$rootScope", "$state", "AuthService", "toast",
    function ($scope, $interval, $timeout, $mdDialog, $q, CurrentPatient, Session, ChunkedRecordingService, TranscriptionAPI, TranscriptionSeatsAPI, $modal, PaymentTransactionsAPI, AppointmentAPI, TranscriptionLiveUpdateService, $rootScope, $state, AuthService, toast) {
        const ctrl = this;
        ctrl.service = ChunkedRecordingService;
        ctrl.CP = CurrentPatient;

        // --- Consult Timer Constants ---
        const STATE_NOT_START = 1;
        const STATE_IN_PROGRESS = 2;
        const STATE_FINISH = 3;

        const OPTOMETRIST = 1;
        const TECHNICIAN = 2;
        const NURSE = 3;
        const DOCTOR = 4;

        // --- Consult Timer State ---
        ctrl.consultState = STATE_NOT_START;
        ctrl.userRole = null;
        let savedPatientAppointment = null;

        // --- UI State ---
        ctrl.timer = "00:00";
        ctrl.microphones = [];
        ctrl.selectedDeviceId = null;
        ctrl.permissionGranted = false;
        ctrl.permissionError = null;
        ctrl.hasRecordingToday = false;
        ctrl.totalSecondsToday = 0;
        ctrl.transcriptionId = null;
        ctrl.currentSessionStartTime = 0;
        ctrl.latestTranscriptionStatus = null;

        // New State for processing lock
        ctrl.isRecentProcessing = false;
        let statusPollingInterval = null;

        let timerInterval = null;
        let secondsElapsed = 0;

        // --- Lifecycle ---
        ctrl.$onInit = function () {
            checkUserRole();
            if (ctrl.CP.patient && ctrl.CP.patient.appointment) {
                checkAppointmentInProgress(ctrl.CP.patient.appointment);
                savedPatientAppointment = angular.copy(ctrl.CP.patient.appointment);
            }

            // Recorder Init
            checkPermissions();
            checkTodayTranscriptions();

            if (navigator.mediaDevices) {
                navigator.mediaDevices.ondevicechange = function () {
                    if (ctrl.permissionGranted) loadDevices();
                };
            }

            $scope.$watch(() => ctrl.service.isRecording, (isRecording) => {
                if (isRecording) {
                    ctrl.isPendingStart = false;
                    ctrl.isStopping = false;
                    ctrl.isResumingAction = false;
                    startTimer(ctrl.currentSessionStartTime);

                    if (ctrl.consultState === STATE_NOT_START) {
                        ctrl.consultState = STATE_IN_PROGRESS;
                        updateAppointment(true);
                    }
                } else {
                    ctrl.isPendingStart = false;
                    ctrl.isStopping = false;
                    ctrl.isResumingAction = false;
                    stopTimer();
                }
            });
        };

        ctrl.$onDestroy = function () {
            stopTimer();
            stopStatusPolling();

            // A start still connecting is abandoned too, or it would open the mic with no UI left.
            // A stop already flushing is left to finish - stopping again would send a second final chunk.
            if ((ctrl.service.isRecording && !ctrl.isStopping) || ctrl.service.isConnecting) {
                ctrl.service.stopSession();
            }

            setTimeout(() => {
                if (savedPatientAppointment && ctrl.consultState === STATE_IN_PROGRESS) {
                    finishAppointmentInSilent();
                }
            }, 100);
        };

        // The patient header asks for this instead of destroying and re-creating the recorder,
        // which stopped any recording in progress and marked the appointment finished.
        $scope.$on('transcription-recorder-refresh', function () {
            if (!ctrl.service.isRecording && !ctrl.service.isConnecting && !ctrl.isStopping) {
                checkTodayTranscriptions();
            }
        });

        // --- Leaving the dashboard mid-recording ---
        //
        // The recorder only lives on the patient dashboard, so any state change destroys it and ends
        // the recording. Ask first, and when the user agrees, stop the session and wait for its final
        // chunk to upload before navigating, so the end of the consult isn't lost.
        let pendingLeave = null;
        let allowLeave = false;

        const deregisterStateChangeStart = $rootScope.$on("$stateChangeStart", function (event, toState, toParams) {
            if (allowLeave || event.defaultPrevented) return;
            // Logging out clears the session before navigating - nothing is left to upload with,
            // and $onDestroy releases the microphone.
            if (!AuthService.isAuthenticated()) return;
            if (!ctrl.service.isRecording && !ctrl.service.isConnecting) return;
            if (ctrl.isStopping) return; // Already ending; let the destroy path finish it.

            event.preventDefault();
            pendingLeave = { stateName: toState.name, params: toParams };
            showLeaveTranscriptionModal();
        });

        $scope.$on("$destroy", deregisterStateChangeStart);

        function showLeaveTranscriptionModal() {
            if ($scope.leaveTranscriptionModal) return;

            $scope.isEndingTranscription = false;
            $scope.leaveTranscriptionModal = $modal({
                scope: $scope,
                templateUrl: "app/src/views/templates/modal.transcription-leave.tpl.html",
                show: true,
                keyboard: false,
                backdrop: "static",
                onHide: function () {
                    angular.element("body").removeClass("modal-open").css("overflow", "auto");
                    $scope.leaveTranscriptionModal = null;
                }
            });
        }

        $scope.stayOnDashboard = function () {
            pendingLeave = null;
            if ($scope.leaveTranscriptionModal) {
                $scope.leaveTranscriptionModal.hide();
            }
        };

        $scope.endTranscriptionAndLeave = function () {
            if (!pendingLeave || $scope.isEndingTranscription) return;

            const destination = pendingLeave;
            const currentTranscriptionId = ctrl.service.transcriptionId || ctrl.transcriptionId;
            $scope.isEndingTranscription = true;
            ctrl.isStopping = true;
            stopTimer();

            const leave = function () {
                ctrl.isStopping = false;
                pendingLeave = null;
                allowLeave = true;
                if ($scope.leaveTranscriptionModal) {
                    $scope.leaveTranscriptionModal.hide();
                }
                $state.go(destination.stateName, destination.params);
            };

            $q.when(ctrl.service.stopSession()).then(function () {
                if (currentTranscriptionId) {
                    announceTranscriptionCompleted(currentTranscriptionId);
                }
            }).catch(function (err) {
                console.error("Error flushing final chunk", err && err.status);
            }).finally(leave);
        };

        // Reaches the transcription processor, which sits outside this component's parents.
        function announceTranscriptionCompleted(transcriptionId) {
            $rootScope.$broadcast('transcription-completed', transcriptionId);
        }

        // Watch for appointment changes.
        $scope.$watch(() => ctrl.CP.patient ? ctrl.CP.patient.appointment : null, function (newValue, oldValue) {
            if (newValue) {
                if (!oldValue || newValue.patient_appointment_id !== oldValue.patient_appointment_id) {
                    checkAppointmentInProgress(newValue);
                    savedPatientAppointment = angular.copy(newValue);
                }
            }
        }, true);

        // --- Consult Timer API & Logic Methods ---
        function checkUserRole() {
            if (Session.user && Session.user.roles) {
                if (Session.user.roles.some((t) => t === "OPTOMETRIST")) {
                    ctrl.userRole = OPTOMETRIST;
                } else if (Session.user.roles.some((t) => t === "TECHNICIAN")) {
                    ctrl.userRole = TECHNICIAN;
                } else if (Session.user.roles.some((t) => t === "REGISTERED_NURSE" || t === "CLINICAL_NURSE_SPECIALIST")) {
                    ctrl.userRole = NURSE;
                } else if (Session.user.roles.some((t) => t === "REGISTRAR" || t === "OPHTHALMOLOGIST")) {
                    ctrl.userRole = DOCTOR;
                }
            }
        }

        function setAppointmentState(start, finish) {
            if (!start && !finish) {
                ctrl.consultState = STATE_NOT_START;
            } else if (start && !finish) {
                ctrl.consultState = STATE_IN_PROGRESS;
            } else if (finish) {
                ctrl.consultState = STATE_FINISH;
            }
        }

        function checkAppointmentInProgress(appointment) {
            const seen = appointment.patient_seen;
            if (!seen) {
                ctrl.consultState = STATE_NOT_START;
                return;
            }
            if (ctrl.userRole === OPTOMETRIST) {
                setAppointmentState(seen["optometrist_started_timestamp"], seen["optometrist_finished_timestamp"]);
            } else if (ctrl.userRole === TECHNICIAN) {
                setAppointmentState(seen["technician_started_timestamp"], seen["technician_finished_timestamp"]);
            } else if (ctrl.userRole === NURSE) {
                setAppointmentState(seen["nurse_started_timestamp"], seen["nurse_finished_timestamp"]);
            } else if (ctrl.userRole === DOCTOR) {
                setAppointmentState(seen["doctor_started_timestamp"], seen["doctor_finished_timestamp"]);
            }
        }

        function updateAppointment(isStart) {
            if (!ctrl.CP.patient || !ctrl.CP.patient.appointment) {
                return;
            }

            const body = angular.copy(ctrl.CP.patient.appointment);
            const now = new Date().toISOString();

            if (!body.patient_seen) {
                body.patient_seen = {};
            }

            if (ctrl.userRole === OPTOMETRIST) {
                if (isStart) {
                    body.patient_seen.optometrist_started = true;
                    body.patient_seen.optometrist_started_timestamp = now;
                    body.patient_seen.optometrist_finished = false;
                    body.patient_seen.optometrist_finished_timestamp = null;
                } else {
                    body.patient_seen.optometrist_finished = true;
                    body.patient_seen.optometrist_finished_timestamp = now;
                }
            } else if (ctrl.userRole === TECHNICIAN) {
                if (isStart) {
                    body.patient_seen.technician_started = true;
                    body.patient_seen.technician_started_timestamp = now;
                    body.patient_seen.technician_finished = false;
                    body.patient_seen.technician_finished_timestamp = null;
                } else {
                    body.patient_seen.technician_finished = true;
                    body.patient_seen.technician_finished_timestamp = now;
                }
            } else if (ctrl.userRole === NURSE) {
                if (isStart) {
                    body.patient_seen.nurse_started = true;
                    body.patient_seen.nurse_started_timestamp = now;
                    body.patient_seen.nurse_finished = false;
                    body.patient_seen.nurse_finished_timestamp = null;
                } else {
                    body.patient_seen.nurse_finished = true;
                    body.patient_seen.nurse_finished_timestamp = now;
                }
            } else if (ctrl.userRole === DOCTOR) {
                if (isStart) {
                    body.patient_seen.doctor_started = true;
                    body.patient_seen.doctor_started_timestamp = now;
                    body.patient_seen.doctor_finished = false;
                    body.patient_seen.doctor_finished_timestamp = null;
                } else {
                    body.patient_seen.doctor_finished = true;
                    body.patient_seen.doctor_finished_timestamp = now;
                }
            }

            AppointmentAPI.updateAppointment(
                ctrl.CP.patient.appointment.patient_id,
                ctrl.CP.patient.appointment.patient_appointment_id,
                body
            ).then(
                function (resp) {
                    ctrl.CP.patient.appointment = angular.copy(resp.data);
                    savedPatientAppointment = angular.copy(ctrl.CP.patient.appointment);
                    checkAppointmentInProgress(ctrl.CP.patient.appointment);
                },
                function (error) {
                    console.error("Failed to update appointment:", error && error.status);
                }
            );
        }

        function finishAppointmentInSilent() {
            if (!savedPatientAppointment) return;

            // Built from the appointment it is sent to - by now CurrentPatient may hold someone else.
            const body = angular.copy(savedPatientAppointment);
            const now = new Date().toISOString();

            if (!body.patient_seen) {
                body.patient_seen = {};
            }

            if (ctrl.userRole === OPTOMETRIST) {
                body.patient_seen.optometrist_finished = true;
                body.patient_seen.optometrist_finished_timestamp = now;
            } else if (ctrl.userRole === TECHNICIAN) {
                body.patient_seen.technician_finished = true;
                body.patient_seen.technician_finished_timestamp = now;
            } else if (ctrl.userRole === NURSE) {
                body.patient_seen.nurse_finished = true;
                body.patient_seen.nurse_finished_timestamp = now;
            } else if (ctrl.userRole === DOCTOR) {
                body.patient_seen.doctor_finished = true;
                body.patient_seen.doctor_finished_timestamp = now;
            }

            AppointmentAPI.updateAppointment(
                savedPatientAppointment.patient_id,
                savedPatientAppointment.patient_appointment_id,
                body
            );
        }

        $scope.openTranscriptionModal = () => {
            window.scrollTo({ top: 0, behavior: 'instant' });
            let activeTranscription = null;

            if (ctrl.service.isRecording || ctrl.isRecentProcessing) {
                activeTranscription = {
                    transcription_id: ctrl.service.transcriptionId || ctrl.transcriptionId,
                    date_created: new Date(),
                    isLive: true
                };
            }

            $mdDialog.show({
                controller: "TranscriptionModalController",
                templateUrl: "app/src/js/components/transcription/transcription-modal.html",
                parent: angular.element(document.body),
                clickOutsideToClose: false,
                scope: $scope.$new(true),
                locals: {
                    userId: ctrl.userId || (Session && Session.user ? Session.user.id : null),
                    patientId: ctrl.patientId || (ctrl.CP && ctrl.CP.patient ? ctrl.CP.patient.id : null),
                    practiceId: ctrl.practiceId || (Session && Session.practice ? Session.practice.id : null),
                    activeTranscription: activeTranscription,
                    recentTranscriptionId: ctrl.transcriptionId,
                    recentLocalText: ctrl.service.rawText
                }
            });
        };

        // --- COMPLETION ---
        //
        // The completer Lambda publishes a push event the moment it writes the final text, and that
        // is what normally ends the wait. The poll below is the fallback for a missed event: it used
        // to ask once a second for two minutes, which made it the most expensive poll in the app, and
        // now asks every fifteen seconds over the same window.
        ctrl.pollFailed = false;
        const STATUS_POLL_INTERVAL_MS = 15000;
        const MAX_POLL_ATTEMPTS = 8;  // 8 x 15 s = two minutes, as before
        const MAX_POLL_ERRORS = 3;    // Maximum consecutive network failures
        let pollAttempts = 0;
        let pollErrorCount = 0;
        let followedTranscriptionId = null;

        TranscriptionLiveUpdateService.subscribe($scope, {
            transcriptionId: function () {
                return followedTranscriptionId;
            },
            onFinished: function (payload) {
                ctrl.latestTranscriptionStatus = payload.status;
                if (payload.status === 'COMPLETED') {
                    announceTranscriptionCompleted(followedTranscriptionId);
                    ctrl.isRecentProcessing = false;
                    stopStatusPolling();
                    checkTodayTranscriptions(); // Refresh timer
                } else if (payload.status === 'FAILED') {
                    handlePollFailure("The previous transcription failed to process correctly.");
                }
            },
        });

        function startStatusPolling(tId) {
            if (statusPollingInterval) return;
            followedTranscriptionId = tId;
            ctrl.isRecentProcessing = true;
            ctrl.pollFailed = false; // Reset on new poll
            ctrl.showTimeoutError = false;
            pollAttempts = 0;
            pollErrorCount = 0;

            statusPollingInterval = $interval(function () {
                pollAttempts++;

                if (pollAttempts > MAX_POLL_ATTEMPTS) {
                    stopStatusPolling();
                    ctrl.isRecentProcessing = false;
                    ctrl.showTimeoutError = true;
                    return;
                }

                const pId = ctrl.practiceId || (Session && Session.practice ? Session.practice.id : null);
                if (!pId || !tId) return;

                TranscriptionAPI.getTranscription(pId, tId)
                    .then(function (resp) {
                        pollErrorCount = 0; // Reset network errors on successful request
                        const status = resp.data.transcription_status;
                        ctrl.latestTranscriptionStatus = status;

                        if (status === 'COMPLETED') {
                            announceTranscriptionCompleted(tId);
                            ctrl.isRecentProcessing = false;
                            stopStatusPolling();
                            checkTodayTranscriptions(); // Refresh timer
                        } else if (status === 'FAILED') {
                            handlePollFailure("The previous transcription failed to process correctly.");
                        }
                    })
                    .catch(function (err) {
                        console.error("Failed to poll recent transcription status:", err && err.status);
                        pollErrorCount++;

                        if (pollErrorCount >= MAX_POLL_ERRORS) {
                            handlePollFailure("Lost connection while checking transcription status. Please check your network.");
                        }
                    });
            }, STATUS_POLL_INTERVAL_MS);
        }

        function stopStatusPolling() {
            followedTranscriptionId = null;
            if (statusPollingInterval) {
                $interval.cancel(statusPollingInterval);
                statusPollingInterval = null;
            }
        }

        function handlePollFailure(errorMessage) {
            stopStatusPolling();
            ctrl.isRecentProcessing = false;
            ctrl.pollFailed = true; // Lock out the resume function
        }

        function checkTodayTranscriptions() {
            const pId = ctrl.patientId || (ctrl.CP && ctrl.CP.patient ? ctrl.CP.patient.id : null);
            if (!pId) return;
            const practiceId = ctrl.practiceId || (Session && Session.practice ? Session.practice.id : null);
            const userId = (Session && Session.user ? Session.user.id : null);

            TranscriptionAPI.getTranscriptionsByPatientId(practiceId, pId)
                .then(function (response) {
                    // Answered for a patient this recorder has since moved off.
                    if (!ctrl.patientId && (!ctrl.CP.patient || ctrl.CP.patient.id !== pId)) return;

                    const list = response.data || [];
                    const today = new Date().toDateString();

                    // Only today's recordings by this user: transcriptions are private, and resuming
                    // must never append to another clinician's recording.
                    const todaysRecordings = list.filter(t => {
                        const isToday = new Date(t.date_created).toDateString() === today;
                        const isMine = userId != null && t.created_by_user_id == userId;
                        return isMine && isToday;
                    });

                    if (todaysRecordings.length > 0) {
                        ctrl.hasRecordingToday = true;
                        // Sort to get the most recent transcription first
                        todaysRecordings.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));

                        // Since we already filtered out unwanted records, we can safely grab the first one
                        const latestTranscription = todaysRecordings[0];
                        const duration = latestTranscription.duration_seconds || latestTranscription.durationSeconds || 0;

                        ctrl.totalSecondsToday = parseInt(duration) || 0;
                        ctrl.transcriptionId = latestTranscription.transcription_id || latestTranscription.transcriptionId;

                        const status = latestTranscription.transcription_status || latestTranscription.transcriptionStatus;
                        ctrl.latestTranscriptionStatus = status;

                        if (status !== 'COMPLETED' && status !== 'FAILED') {
                            ctrl.isRecentProcessing = true;
                            startStatusPolling(ctrl.transcriptionId);
                        } else {
                            ctrl.isRecentProcessing = false;
                            stopStatusPolling();
                        }

                        if (!ctrl.service.isRecording && !ctrl.isStopping) {
                            ctrl.timer = formatTime(ctrl.totalSecondsToday);
                        }
                    } else {
                        ctrl.hasRecordingToday = false;
                        ctrl.totalSecondsToday = 0;
                        ctrl.transcriptionId = null;
                        ctrl.isRecentProcessing = false;
                        stopStatusPolling();

                        if (!ctrl.service.isRecording) {
                            ctrl.timer = "00:00";
                        }
                    }
                });
        }

        function formatTime(totalSeconds) {
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return (minutes < 10 ? "0" + minutes : minutes) + ":" +
                (seconds < 10 ? "0" + seconds : seconds);
        }

        function checkPermissions() {
            if (navigator.permissions && navigator.permissions.query) {
                navigator.permissions.query({ name: 'microphone' })
                    .then(function (permissionStatus) {
                        if (permissionStatus.state === 'granted') {
                            ctrl.permissionGranted = true;
                            loadDevices();
                        }
                    }).catch(() => { console.log("Could not find microphone.")});
            }
        }

        ctrl.requestPermissions = function () {
            return navigator.mediaDevices.getUserMedia({ audio: true })
                .then(function (stream) {
                    ctrl.permissionGranted = true;
                    ctrl.permissionError = null;
                    stream.getTracks().forEach(track => track.stop());
                    return loadDevices();
                })
                .catch(function (err) {
                    console.error("Microphone permission denied:", err);
                    ctrl.permissionGranted = false;
                    ctrl.permissionError = "Microphone access denied.";
                    $scope.$applyAsync();
                    throw err;
                });
        };

        function loadDevices() {
            return navigator.mediaDevices.enumerateDevices()
                .then(function (devices) {
                    const audioInputs = devices.filter(d => d.kind === 'audioinput');
                    $timeout(function () {
                        ctrl.microphones = audioInputs;
                        if (!ctrl.selectedDeviceId && ctrl.microphones.length > 0) {
                            const defaultDevice = ctrl.microphones.find(m => m.deviceId === 'default');
                            ctrl.selectedDeviceId = defaultDevice ? defaultDevice.deviceId : ctrl.microphones[0].deviceId;
                        }
                    });
                });
        }

        function startTimer(startFromSeconds) {
            stopTimer();
            secondsElapsed = startFromSeconds || 0;
            ctrl.timer = formatTime(secondsElapsed);
            timerInterval = $interval(function () {
                secondsElapsed++;
                ctrl.timer = formatTime(secondsElapsed);
            }, 1000);
        }

        function stopTimer() {
            if (timerInterval) {
                $interval.cancel(timerInterval);
                timerInterval = null;
            }
        }

        function getEntitlement() {
            return TranscriptionSeatsAPI.getMyEntitlement()
                .then(function (response) {
                    return response ? response.data : null;
                })
                .catch(function (err) {
                    console.error("Failed to load transcription entitlement", err && err.status);
                    return null;
                });
        }

        function checkSufficientTokens() {
            // The server resolves the user's own trial seat, paid seat and the practice wallet in
            // one place - the recorder just asks whether a recording may start.
            return getEntitlement().then(function (entitlement) {
                return !!(entitlement && entitlement.can_start_transcription);
            });
        }

        function showInsufficientTokensModal() {
            if ($scope.serverErrorModal) {
                $scope.serverErrorModal.hide();
            }

            $scope.alertMessage = "Practice does not have enough tokens to begin a transcription. Please configure your Payment Settings to top up your balance.";

            $scope.serverErrorModal = $modal({
                scope: $scope,
                templateUrl: "app/src/views/templates/modal.server-error.tpl.html",
                show: true,
                title: "Insufficient Tokens",
                keyboard: false,
                backdrop: "static",
                onHide: function () {
                    angular.element("body").removeClass("modal-open").css("overflow", "auto");
                    $scope.alertMessage = null;

                    // Open the Payment Transactions modal after the error modal is closed.
                    $mdDialog.show({
                        controller: "paymentTransactionsController",
                        templateUrl: "app/src/js/components/paymentTransactions/payment-transactions.tmpl.html",
                        parent: angular.element(document.body),
                        clickOutsideToClose: false,
                        openFrom: "#anchor",
                        closeTo: "#anchor",
                        scope: $scope.$new(true),
                        preserveScope: true,
                        resolve: {},
                    });
                },
            });
        }

        function initiateRecording(transcriptionIdToUse, startingSequenceIndex = 0) {
            if (ctrl.service.isRecording || ctrl.service.isConnecting || ctrl.isRecentProcessing) return;
            const pId = ctrl.patientId || (ctrl.CP && ctrl.CP.patient ? ctrl.CP.patient.id : null);
            ctrl.isPendingStart = true;

            const permissionPromise = ctrl.permissionGranted ? Promise.resolve() : ctrl.requestPermissions();

            permissionPromise.then(function () {
                ctrl.service.startSession(
                    ctrl.practiceId,
                    pId,
                    transcriptionIdToUse,
                    ctrl.selectedDeviceId,
                    startingSequenceIndex,
                    // startSession's last argument is its error callback.
                    (err) => {
                        console.error("Recorder error", err && (err.status || err.name));
                        ctrl.isPendingStart = false;
                        ctrl.isResumingAction = false;
                        $scope.$applyAsync();
                    }
                );
            }).catch(function (err) {
                console.warn("Recording cancelled or permission denied.", err && err.name);
                ctrl.isPendingStart = false;
                ctrl.isResumingAction = false;
                $scope.$applyAsync();
            });
        }

        // --- Trial Modal & Recording Flow ---

        // Extracted the core recording logic into a reusable function
        function proceedWithRecording() {
            ctrl.isPendingStart = true;
            ctrl.pollFailed = false;
            ctrl.showTimeoutError = false;

            checkSufficientTokens().then(function (hasTokens) {
                if (!hasTokens) {
                    ctrl.isPendingStart = false;
                    showInsufficientTokensModal();
                    return;
                }

                ctrl.isResumingAction = false;
                ctrl.currentSessionStartTime = 0;

                initiateRecording(null, 0);
            });
        }

        function showTrialModal() {
            $scope.trialModal = $modal({
                scope: $scope,
                templateUrl: "app/src/views/templates/modal.transcription-trial.tpl.html",
                show: true,
                keyboard: false,
                backdrop: "static",
                onHide: function () {
                    angular.element("body").removeClass("modal-open").css("overflow", "auto");
                }
            });
        }

        $scope.continueWithoutTrial = function () {
            if ($scope.trialModal) {
                $scope.trialModal.hide();
            }

            ctrl.isPendingStart = true;

            // Declining the trial means paying from the practice wallet, so the wallet itself must
            // hold enough tokens - the unclaimed trial the user just declined cannot vouch for them.
            getEntitlement().then(function (entitlement) {
                if (!entitlement || !(entitlement.estimated_wallet_minutes_remaining > 0)) {
                    ctrl.isPendingStart = false;
                    showInsufficientTokensModal();
                    $scope.$applyAsync();
                    return;
                }

                ctrl.pollFailed = false;
                ctrl.showTimeoutError = false;
                ctrl.isResumingAction = false;
                ctrl.currentSessionStartTime = 0;

                initiateRecording(null, 0);
            });
        };

        $scope.startTrialAndRecord = function () {
            if ($scope.trialModal) {
                $scope.trialModal.hide();
            }

            ctrl.isPendingStart = true; // Show spinner while trial activates
            const pId = ctrl.practiceId || (Session && Session.practice ? Session.practice.id : null);

            TranscriptionAPI.startTrial(pId)
                .then(function (resp) {
                    proceedWithRecording();
                })
                .catch(function (err) {
                    console.error("Failed to start transcription trial", err && err.status);
                    ctrl.isPendingStart = false;
                    
                    // Fallback: Attempt to proceed anyway in case they have tokens
                    proceedWithRecording(); 
                });
        };

        // Updated Entry Point
        ctrl.startNewRecording = function () {
            if (ctrl.isRecentProcessing) return;

            ctrl.isPendingStart = true;

            // The free trial is per user: the modal is offered until this user's own trial seat
            // exists, regardless of what anyone else in the practice has done.
            getEntitlement().then(function (entitlement) {
                ctrl.isPendingStart = false;

                if (entitlement && entitlement.is_trial_active && !entitlement.seat_tier) {
                    showTrialModal();
                } else {
                    proceedWithRecording();
                }
            });
        };

        ctrl.resumeRecording = function () {
            if (ctrl.isRecentProcessing) return;

            // Block resume if the file processing failed or timed out
            if (ctrl.pollFailed) {
                handlePollFailure("Cannot resume because the previous transcription failed or timed out. Please start a new recording.");
                return;
            }

            ctrl.isResumingAction = true;
            ctrl.isPendingStart = true;
            ctrl.showTimeoutError = false;

            checkSufficientTokens().then(function (hasTokens) {
                if (!hasTokens) {
                    ctrl.isPendingStart = false;
                    ctrl.isResumingAction = false;
                    showInsufficientTokensModal();
                    return;
                }

                let resumeTime = ctrl.totalSecondsToday;
                if (secondsElapsed > resumeTime) {
                    resumeTime = secondsElapsed;
                }
                ctrl.currentSessionStartTime = resumeTime;

                ctrl.service.isConnecting = true;

                if (ctrl.transcriptionId) {
                    TranscriptionAPI.getTranscription(Session.practice.id, ctrl.transcriptionId)
                        .then(function (response) {
                            const existingText = response.data.raw_text || "";
                            const chunkList = response.data.transcription_chunk_list || [];

                            let nextSequenceIndex = 0;
                            if (chunkList.length > 0) {
                                const maxIndex = Math.max(...chunkList.map(c => c.sequence_index));
                                nextSequenceIndex = maxIndex + 1;
                            }

                            ctrl.service.isConnecting = false;
                            initiateRecording(ctrl.transcriptionId, nextSequenceIndex);
                        })
                        .catch(function (err) {
                            // Without the saved chunks there is no knowing where to number from. Starting
                            // at 0 reused numbers already saved, and the API drops a chunk it already has,
                            // so everything recorded until the numbers caught up was silently lost.
                            console.error("Failed to load previous text", err && err.status);
                            ctrl.service.isConnecting = false;
                            ctrl.isPendingStart = false;
                            ctrl.isResumingAction = false;
                            toast.create({
                                timeout: 6000,
                                message: "Couldn't load the recording to resume it. Please try again.",
                                className: "alert-danger",
                                dismissible: true
                            });
                        });
                } else {
                    ctrl.service.isConnecting = false;
                    initiateRecording(null, 0);
                }
            });
        };

        ctrl.stopRecording = function () {
            if (!ctrl.service.isRecording) return;

            ctrl.isStopping = true;
            ctrl.isRecentProcessing = true; // Hard-lock UI while the final chunk flushes
            stopTimer();

            const currentTranscriptionId = ctrl.service.transcriptionId || ctrl.transcriptionId;
            const pId = ctrl.practiceId || (Session && Session.practice ? Session.practice.id : null);

            // Wait for the final audio chunk to finish flushing (which triggers isLastChunk=true)
            ctrl.service.stopSession().then(function () {
                if (currentTranscriptionId && pId) {
                    ctrl.hasRecordingToday = true;

                    // Emit event so other UI components know recording stopped
                    announceTranscriptionCompleted(currentTranscriptionId);

                    // Force polling immediately on the exact ID we just completed
                    startStatusPolling(currentTranscriptionId);
                    checkTodayTranscriptions();
                } else {
                    ctrl.hasRecordingToday = true;
                }
            }).catch(function (err) {
                console.error("Error flushing final chunk", err && err.status);
                ctrl.isRecentProcessing = false;
            }).finally(function () {
                // The physical recording has stopped, remove the specific `isStopping` lock
                ctrl.isStopping = false;
                $scope.$applyAsync();
            });
        };

        // --- UI Helpers ---
        ctrl.isActivelyRecording = function () {
            // True ONLY if the microphone is hot, we aren't booting it up, and the user hasn't clicked stop.
            return ctrl.service.isRecording && !ctrl.service.isConnecting && !ctrl.isStopping;
        };

        ctrl.isBusyProcessing = function () {
            // True if we are waiting for the API, flushing the buffer, or requesting mic permissions.
            return ctrl.isRecentProcessing || ctrl.isStopping || ctrl.service.isConnecting || ctrl.isPendingStart;
        };

        // Add this function to handle the refresh action
        ctrl.refreshStatus = function () {
            ctrl.pollFailed = false;
            ctrl.showTimeoutError = false;
            ctrl.isRecentProcessing = true; // Show loading state

            // Re-run the initial check to fetch the latest state from the server
            // This will automatically restart polling if it's still processing
            checkTodayTranscriptions();
        };

        // Add this helper to determine when to show the refresh button
        ctrl.canShowRefresh = function () {
            return !ctrl.isActivelyRecording() &&
                ctrl.hasRecordingToday &&
                (ctrl.pollFailed || ctrl.showTimeoutError);
        };

        ctrl.canShowResume = function () {
            if (ctrl.isActivelyRecording() || !ctrl.hasRecordingToday) return false;

            // Hide if the poll explicitly timed out/failed or the file status is FAILED
            if (ctrl.pollFailed || ctrl.showTimeoutError || ctrl.latestTranscriptionStatus === 'FAILED') return false; // Added ctrl.showTimeoutError here

            // ZOMBIE CHECK: Hide if the status is not COMPLETED, but the system is NOT actively processing it
            if (ctrl.latestTranscriptionStatus !== 'COMPLETED' && !ctrl.isRecentProcessing) return false;

            return true;
        };
    }
]);